import React, { useEffect, useState } from "react";
import styles from "./style/SocialPostingBox.module.css";
import {
    PLATFORM_IMG_MAPPING,
    PLATFORM_IMG_MAPPING_NEW,
} from "../content_calender_new/SocialMediaCards";
import { startCase, upperCase } from "lodash";
import {
    Button,
    Collapse,
    DatePicker,
    Space,
    Input,
    Form,
    message,
    Modal,
    Popconfirm,
    Alert,
} from "antd";
import dayjs from "dayjs";
import {
    CheckOutlined,
    CloseOutlined,
    PlusOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { useBotEndpoint } from "../_actions/bot";
import { useItemsFine, useDeleteItemMutation } from "../_actions/app/items";
import SocialAccountDropdown from "./SocialAccountDropdown";
import SocialAccountSelection from "./SocialAccountSelection";
import { CaptionBox } from "../content_calender_new/add_post/ExternalPostPanel";
import { queryClient } from "../_helpers/query";

const buildSubtitle = (platform, socialKeys, successSK) => {
    const pTime =
        platform?.scheduled_time ||
        platform?.posting_time ||
        platform?.posting_dt;
    const accountId = platform?.posting_account;

    let timePart = "";
    if (pTime) {
        timePart = dayjs(pTime).format("ddd H:mm");
    }

    let accountPart = "";
    if (accountId && successSK && socialKeys?.length > 0) {
        const account = socialKeys.find((k) => k.id === accountId);
        if (account?.profile_info) {
            const info =
                typeof account.profile_info === "string"
                    ? JSON.parse(account.profile_info)
                    : account.profile_info;
            const handle = info?.username || info?.name || info?.handle || "";
            if (handle) {
                accountPart = handle.startsWith("@") ? handle : `@${handle}`;
            }
        }
    }

    if (!timePart && !accountPart) return "Not connected";
    if (timePart && accountPart) return `${timePart} • ${accountPart}`;
    return timePart || accountPart;
};

const mapStatusDisplay = (raw) => {
    if (!raw) return "DRAFT";
    const s = raw.toUpperCase();
    if (s === "IDLE" || s === "DRAFT") return "DRAFT";
    if (s === "APPROVED") return "APPROVED";
    if (s === "READY_TO_PUBLISH") return "SCHEDULED";
    if (s === "PUBLISHED") return "POSTED";
    return s;
};

function SocialPostingBox({ platforms = [], entry_id, org_id }) {
    const {
        isSuccess: successSK,
        isLoading: loadingSK,
        data: dataSK,
        refetch: refetchSK,
    } = useItemsFine(
        "qdb_social_keys",
        `filter[organization][_eq]=${org_id}&fields=id,platform,profile_info`,
        true,
    );

    const [open, setOpen] = useState(false);
    const [isPostNow, setIsPostNow] = useState(false);

    return (
        <div className={styles.wrapper}>
            {platforms?.length > 0 && (
                <Collapse
                    accordion
                    items={platforms.map((platform) => {
                        const pStatusRaw =
                            platform?.status || platform?.post_status;
                        const displayStatus = mapStatusDisplay(pStatusRaw);
                        const rawType =
                            platform?.post_type ||
                            platform?.qdb_content_type_id ||
                            "INSTAGRAM_POST";
                        const pType = rawType.toUpperCase();
                        const pTypeLower = rawType.toLowerCase();

                        const statusClass =
                            styles[`status_${displayStatus.toLowerCase()}`] ||
                            "";
                        return {
                            key: platform?.id,
                            label: (
                                <div className={styles.header}>
                                    <div className={styles.name_wrapper}>
                                        <img
                                            key={platform?.id}
                                            src={`/images_rte/icons/${PLATFORM_IMG_MAPPING[pTypeLower]}.png`}
                                            alt={pTypeLower}
                                            className={styles.platformIcon}
                                        />
                                        <div className={styles.platform_info}>
                                            <p className={styles.platform_name}>
                                                {startCase(pTypeLower)}
                                            </p>
                                            <p
                                                className={
                                                    styles.platform_subtitle
                                                }
                                            >
                                                {buildSubtitle(
                                                    platform,
                                                    dataSK,
                                                    successSK,
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                    <span
                                        className={`${styles.status} ${statusClass}`}
                                    >
                                        {startCase(displayStatus.toLowerCase())}
                                    </span>
                                </div>
                            ),
                            children: (
                                <OnePlatform
                                    platform={platform}
                                    entry_id={entry_id}
                                    org_id={org_id}
                                    social_acconts={
                                        successSK
                                            ? dataSK.filter((row) =>
                                                  pTypeLower?.includes(
                                                      row.platform?.toLowerCase(),
                                                  ),
                                              )
                                            : []
                                    }
                                />
                            ),
                        };
                    })}
                />
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                <Button
                    // type="primary"
                    block
                    onClick={() => {
                        setIsPostNow(true);
                        setOpen(true);
                    }}
                >
                    Post Now
                </Button>
                <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setIsPostNow(false);
                        setOpen(true);
                    }}
                >
                    Schedule New
                </Button>
            </div>
            <SocialPostingModal
                open={open}
                setOpen={setOpen}
                entry_id={entry_id}
                org_id={org_id}
                isPostNow={isPostNow}
            />
        </div>
    );
}

export default SocialPostingBox;

function OnePlatform({ platform, entry_id, org_id, social_acconts }) {
    const pStatusRaw = platform?.status || platform?.post_status;
    const pStatus = pStatusRaw ? pStatusRaw.toUpperCase() : "DRAFT";
    const rawType =
        platform?.post_type ||
        platform?.qdb_content_type_id ||
        "INSTAGRAM_POST";
    const pType = rawType.toUpperCase();
    const pTypeLower = rawType.toLowerCase();
    const pTime =
        platform?.scheduled_time ||
        platform?.posting_time ||
        platform?.posting_dt;
    const pCaption = platform?.platform_payload?.caption || platform?.caption;

    const [posting_date, setPostingDate] = useState(null);
    const [caption, setCaption] = useState("");
    const [selectedAccount, setSelectedAccount] = useState(null);

    const mutationEndpoint = useBotEndpoint(
        `/social_post/schedule_for_platforms/${entry_id}`,
        (result) => {
            message.success("Platform updated successfully!");
            queryClient.invalidateQueries(["items", "qdb_calendar_entries"]);
        },
    );

    const deleteMutation = useDeleteItemMutation(() => {
        message.success("Platform deleted successfully!");
        queryClient.invalidateQueries(["items", "qdb_calendar_entries"]);
    });

    const disabledDate = (current) => {
        const today = dayjs().startOf("day");
        const oneMonthLater = dayjs().add(1, "month").endOf("day");
        return current < today || current > oneMonthLater;
    };

    const disabledTime = (current) => {
        // if the date is not today -> enable all hours and minutes
        if (!current || !current.isSame(dayjs(), "day")) {
            return {};
        }
        const currentHour = dayjs().hour();
        const currentMinute = dayjs().minute();
        return {
            disabledHours: () => {
                // disable all hours before current hour
                return Array.from({ length: currentHour }, (_, i) => i);
            },
            disabledMinutes: (selectedHour) => {
                // if selected hour is current hour, disable min. -> till the current minute
                if (selectedHour === currentHour) {
                    return Array.from(
                        { length: currentMinute + 1 },
                        (_, i) => i,
                    );
                }
                return [];
            },
        };
    };

    useEffect(() => {
        if (pCaption) {
            setCaption(pCaption);
        }
        if (pTime) {
            setPostingDate(dayjs(pTime));
        }
        if (platform?.posting_account) {
            setSelectedAccount(platform?.posting_account);
        }
    }, [platform, pCaption, pTime]);

    return (
        <Form layout="vertical">
            {platform?.status === "FAILED" && (
                <Alert
                    type="error"
                    message={platform?.error_log}
                    // description={platform?.error_log}
                    showIcon
                    style={{ marginBottom: "10px" }}
                />
            )}
            <Form.Item label="Posting Date">
                <DatePicker
                    format="YYYY-MM-DD HH:mm"
                    disabledDate={disabledDate}
                    showTime={{
                        format: "HH:mm",
                        disabledTime: disabledTime,
                    }}
                    defaultValue={posting_date}
                    value={posting_date}
                    onChange={(date) => setPostingDate(date)}
                />
            </Form.Item>
            <Form.Item label="Social Account">
                <SocialAccountDropdown
                    org_id={org_id}
                    platform={PLATFORM_IMG_MAPPING_NEW[pTypeLower]}
                    value={selectedAccount}
                    onChange={setSelectedAccount}
                    auto_configure={true}
                />
            </Form.Item>
            <Form.Item label="Caption">
                <Input.TextArea
                    placeholder="Enter caption"
                    rows={6}
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                />
            </Form.Item>
            {/* <pre>{JSON.stringify(platform, null, 2)}</pre> */}

            <Space style={{ marginTop: "10px" }}>
                {(pStatus === "IDLE" || pStatus === "DRAFT") && (
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => {
                            mutationEndpoint.mutate({
                                platforms: [
                                    {
                                        qdb_platform_id: platform?.id,
                                        post_type: pType,
                                        posting_account: selectedAccount,
                                        scheduled_time: posting_date
                                            ? posting_date.format()
                                            : null,
                                        status: "APPROVED",
                                        platform_payload: {
                                            caption: caption,
                                            post_type: pType,
                                        },
                                    },
                                ],
                            });
                        }}
                        disabled={!selectedAccount || !entry_id}
                        loading={mutationEndpoint.isLoading}
                    >
                        Approve
                    </Button>
                )}
                {["APPROVED", "PUBLISHED", "FAILED"].includes(pStatus) && (
                    <Button
                        onClick={() => {
                            mutationEndpoint.mutate({
                                platforms: [
                                    {
                                        qdb_platform_id: platform?.id,
                                        post_type: pType,
                                        posting_account: selectedAccount,
                                        scheduled_time: posting_date
                                            ? posting_date.format()
                                            : null,
                                        status: pStatus,
                                        platform_payload: {
                                            caption: caption,
                                            post_type: pType,
                                        },
                                    },
                                ],
                            });
                        }}
                        disabled={!selectedAccount || !entry_id}
                        loading={mutationEndpoint.isLoading}
                        type="primary"
                    >
                        Reschedule
                    </Button>
                )}
                {!["DRAFT", "IDLE", "PUBLISHED", "FAILED"].includes(
                    pStatus,
                ) && (
                    <Button
                        onClick={() => {
                            mutationEndpoint.mutate({
                                platforms: [
                                    {
                                        qdb_platform_id: platform?.id,
                                        post_type: pType,
                                        posting_account: selectedAccount,
                                        scheduled_time: posting_date
                                            ? posting_date.format()
                                            : null,
                                        status: "DRAFT",
                                        platform_payload: {
                                            caption: caption,
                                            post_type: pType,
                                        },
                                    },
                                ],
                            });
                        }}
                        disabled={!entry_id}
                        loading={mutationEndpoint.isLoading}
                    >
                        Cancel
                    </Button>
                )}
                <Popconfirm
                    title="Delete platform"
                    description="Are you sure you want to delete this platform?"
                    onConfirm={() => {
                        deleteMutation.mutate({
                            collection: "qdb_platform_entries",
                            id: platform?.id,
                        });
                    }}
                    okText="Yes"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                >
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        loading={deleteMutation.isLoading}
                    />
                </Popconfirm>
            </Space>
        </Form>
    );
}

function SocialPostingModal({ open, setOpen, entry_id, org_id, isPostNow }) {
    const [posting_date, setPostingDate] = useState(null);
    const [caption, setCaption] = useState("");
    const [selectedAccount, setSelectedAccount] = useState(null);

    useEffect(() => {
        if (open) {
            if (isPostNow) {
                setPostingDate(dayjs());
            } else {
                setPostingDate(null);
            }
        }
    }, [open, isPostNow]);

    const mutationEndpoint = useBotEndpoint(
        `/social_post/schedule_for_platforms/${entry_id}`,
        (result) => {
            message.success("Post scheduled successfully!");
            setOpen(false);
            setCaption("");
            setPostingDate(null);
            setSelectedAccount(null);
            queryClient.invalidateQueries(["items", "qdb_calendar_entries"]);
        },
    );

    const disabledDate = (current) => {
        const today = dayjs().startOf("day");
        const oneMonthLater = dayjs().add(1, "month").endOf("day");
        return current < today || current > oneMonthLater;
    };

    const disabledTime = (current) => {
        if (!current || !current.isSame(dayjs(), "day")) {
            return {};
        }
        const currentHour = dayjs().hour();
        const currentMinute = dayjs().minute();
        return {
            disabledHours: () =>
                Array.from({ length: currentHour }, (_, i) => i),
            disabledMinutes: (selectedHour) => {
                if (selectedHour === currentHour) {
                    return Array.from(
                        { length: currentMinute + 1 },
                        (_, i) => i,
                    );
                }
                return [];
            },
        };
    };

    const handleOk = () => {
        mutationEndpoint.mutate({
            platforms: (Array.isArray(selectedAccount)
                ? selectedAccount
                : [selectedAccount]
            ).map((accountId) => ({
                post_type: "INSTAGRAM_POST",
                posting_account: accountId,
                scheduled_time: posting_date ? posting_date.format() : null,
                status: "APPROVED",
                platform_payload: {
                    caption: caption,
                    post_type: "INSTAGRAM_POST",
                },
            })),
        });
    };

    return (
        <Modal
            open={open}
            title={isPostNow ? "Post Now" : "Schedule New Post"}
            onOk={handleOk}
            okText={isPostNow ? "Post Now" : "Schedule"}
            onCancel={() => setOpen(false)}
            confirmLoading={mutationEndpoint.isLoading}
            okButtonProps={{ disabled: !posting_date || !selectedAccount }}
            destroyOnHidden
        >
            <Form layout="vertical">
                <Form.Item label="Social Account">
                    <SocialAccountSelection
                        org_id={org_id}
                        value={selectedAccount}
                        onChange={setSelectedAccount}
                        auto_configure={true}
                    />
                </Form.Item>
                <Form.Item label="Posting Date">
                    <DatePicker
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD HH:mm"
                        disabledDate={disabledDate}
                        showTime={{
                            format: "HH:mm",
                            disabledTime: disabledTime,
                        }}
                        value={posting_date}
                        onChange={(date) => setPostingDate(date)}
                    />
                </Form.Item>
                <Form.Item label="Caption">
                    <CaptionBox
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
